import React, { useState } from "react"
import styled, { keyframes } from "styled-components"
import { MobergFontFamily } from "../MobergFont/MobergFont"

// An expandable trace of what Clara is doing while she works.
//
// Clara takes two to four seconds to answer. Until now that was a blank pause followed
// by a block of text, which is a large part of why she felt slower than Copilot at UTSW
// despite comparable latency: Copilot starts emitting immediately, so the same wait
// reads as half as long.
//
// Steps complete downward with a check, the running one is dimmed with a spinner, and
// the whole thing settles to "Thought for N seconds" while staying expandable.
//
// The active label is a plain animated opacity rather than a gradient clipped to text.
// The gradient version rendered INVISIBLE in this app, because a mistyped custom
// property made the background-image declaration invalid, leaving transparent text over
// no background. Anything that can fail to nothing is the wrong choice for a status
// indicator: if the effect breaks here, the words still show.

const pulse = keyframes`
	0%, 100% { opacity: 0.55; }
	50%      { opacity: 1; }
`

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(3px); }
	to   { opacity: 1; transform: translateY(0); }
`

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

const spin = keyframes`
	to { transform: rotate(360deg); }
`

/**
 * @param steps       ordered labels, e.g. ["Understanding your question…", …]
 * @param activeIndex which step is running (ignored once `done`)
 * @param done        the answer has landed
 * @param seconds     how long it took, shown in the settled label
 */
export function ThinkingTrace({ steps = [], activeIndex = 0, done = false, seconds = null }) {
	// null means "follow the work": open while running, collapsed once settled. A reader
	// who opens it mid-run keeps it open.
	const [manual, setManual] = useState(null)
	const expanded = manual ?? !done

	if (!steps.length) return null

	const visible = done ? steps.length : Math.min(activeIndex + 1, steps.length)
	const label = done
		? seconds != null
			? `Thought for ${seconds} second${seconds === 1 ? "" : "s"}`
			: "Done"
		: steps[Math.min(activeIndex, steps.length - 1)]

	return (
		<Wrap>
			<Header type="button" aria-expanded={expanded} onClick={() => setManual(current => !(current ?? !done))}>
				<Sparkle viewBox="0 0 24 24" $active={!done} aria-hidden>
					<path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
				</Sparkle>
				<Label $working={!done}>{label}</Label>
				<Chevron viewBox="0 0 24 24" $open={expanded} aria-hidden>
					<path d="M6 9l6 6 6-6" />
				</Chevron>
			</Header>

			<Collapse $open={expanded}>
				<CollapseInner>
					<Trace>
						<Rule aria-hidden />
						<Steps>
							{steps.slice(0, visible).map((step, index) => {
								const complete = done || index < activeIndex
								return (
									<Step key={step} $delay={index * 80}>
										{complete ? (
											<Check viewBox="0 0 24 24" aria-hidden>
												<path d="M20 6L9 17l-5-5" />
											</Check>
										) : (
											<Spinner />
										)}
										<StepText $complete={complete}>{step}</StepText>
									</Step>
								)
							})}
						</Steps>
					</Trace>
				</CollapseInner>
			</Collapse>
		</Wrap>
	)
}

const Wrap = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`

const Header = styled.button`
	display: flex;
	align-items: center;
	gap: 7px;
	width: fit-content;
	margin-left: -6px;
	padding: 3px 7px;
	background: none;
	border: none;
	border-radius: 7px;
	cursor: pointer;
	transition: background-color 120ms ease;

	&:hover {
		background: var(--color-hover-neutral);
	}

	&:focus-visible {
		outline: 2px solid var(--color-accent-interactive);
		outline-offset: 1px;
	}
`

const Sparkle = styled.svg`
	width: 14px;
	height: 14px;
	flex-shrink: 0;
	fill: ${({ $active }) => ($active ? "var(--color-accent-interactive)" : "var(--text-secondary-color)")};
	opacity: ${({ $active }) => ($active ? 1 : 0.7)};
`

const Label = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 13px;
	font-weight: 500;
	white-space: nowrap;
	color: ${({ $working }) => ($working ? "var(--text-color)" : "var(--text-secondary-color)")};
	animation: ${({ $working }) => ($working ? pulse : fadeIn)} ${({ $working }) => ($working ? "1.6s" : "350ms")}
		${({ $working }) => ($working ? "ease-in-out infinite" : "ease-out both")};

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const Chevron = styled.svg`
	width: 13px;
	height: 13px;
	flex-shrink: 0;
	fill: none;
	stroke: var(--text-secondary-color);
	stroke-width: 2.2;
	stroke-linecap: round;
	stroke-linejoin: round;
	transition: transform 300ms cubic-bezier(0.23, 1, 0.32, 1);
	transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`

// grid-template-rows 0fr -> 1fr animates height without measuring, so a step appearing
// mid-transition cannot leave the panel stuck at a stale pixel height.
const Collapse = styled.div`
	display: grid;
	grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
	opacity: ${({ $open }) => ($open ? 1 : 0)};
	transition: grid-template-rows 380ms cubic-bezier(0.23, 1, 0.32, 1), opacity 380ms cubic-bezier(0.23, 1, 0.32, 1);

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const CollapseInner = styled.div`
	overflow: hidden;
`

const Trace = styled.div`
	position: relative;
	margin: 3px 0 0 7px;
	padding-left: 16px;
`

const Rule = styled.span`
	position: absolute;
	left: 0;
	top: 4px;
	bottom: 8px;
	width: 1px;
	background: var(--color-border-default);
`

const Steps = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding: 2px 0;
`

const Step = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
	min-height: 24px;
	animation: ${fadeUp} 320ms cubic-bezier(0.23, 1, 0.32, 1) ${({ $delay }) => $delay}ms both;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`

const Check = styled.svg`
	width: 13px;
	height: 13px;
	flex-shrink: 0;
	fill: none;
	stroke: var(--color-success-text);
	stroke-width: 2.6;
	stroke-linecap: round;
	stroke-linejoin: round;
`

const Spinner = styled.span`
	width: 11px;
	height: 11px;
	flex-shrink: 0;
	border-radius: 50%;
	border: 1.5px solid var(--color-border-default);
	border-top-color: var(--color-accent-interactive);
	animation: ${spin} 700ms linear infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		border-top-color: var(--color-border-default);
	}
`

// Completed steps recede; the one still running stays at full strength. Reading down
// the list should show progress without needing the spinner to say so.
const StepText = styled.span`
	min-width: 0;
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 12.5px;
	color: ${({ $complete }) => ($complete ? "var(--text-secondary-color)" : "var(--text-color)")};
`

export default ThinkingTrace
